import { keyBy } from 'lodash-es';

import { search } from '~/redux/api/methods/evisearch';
import { getGroupNodeChildren } from '~/redux/api/methods/group-nodes/getGroupNodeChildren';
import { GroupNode } from '~/redux/api/methods/group-nodes/types';
import { SearchFilter } from '~/types/DashboardV2';

import { flattenSearchSelectedItemValues } from './flattenSearchSelectedItemValues';

export type EnrichedGroupNode = GroupNode & {
  enrichedFields?: Record<string, any>;
  isPermissionDenied?: boolean;
};

const emptySearchResult = () =>
  Promise.resolve({
    results: [],
    meta: undefined,
  }) as ReturnType<typeof search>;

/*
 * This class implements logic for enriching group nodes with additional fields from evisearch. We
 * can probably make this more generic in the future to support additional sources other than evisearch.
 * I implemented it as a class instead of a pure functions because we its convenient to attach the search
 * filters (ie which fields to return in search results).
 */
export class NodeEnricher {
  private documentFilters: string[] = [];
  private ticketFilters: string[] = [];
  private hasDocumentsAccess: boolean;
  private hasTicketAccess: boolean;

  constructor(
    documentFields: Record<string, SearchFilter>,
    ticketFields: Record<string, SearchFilter>,
    hasDocumentsAccess: boolean,
    hasTicketAccess: boolean,
  ) {
    this.setDocumentFields(documentFields);
    this.setTicketFields(ticketFields);
    this.hasDocumentsAccess = hasDocumentsAccess;
    this.hasTicketAccess = hasTicketAccess;
  }

  setDocumentFields = (documentFields: Record<string, SearchFilter>) => {
    this.documentFilters = Object.values(documentFields).map(
      (field) => field.id,
    );
  };

  setTicketFields = (ticketFields: Record<string, SearchFilter>) => {
    this.ticketFilters = Object.values(ticketFields).map((field) => field.id);
  };

  searchTypeByIds = async (
    type: 'document' | 'ticket',
    ids: (string | number)[],
  ) => {
    const idfield = `${type}_id_${
      type.charAt(0).toUpperCase() + type.slice(1)
    }`;
    const query = [
      {
        type: 'filter',
        id: idfield,
        operator: 'contains_any',
        value: {
          value_list: ids,
        },
      },
    ];

    try {
      return await search({
        type,
        query,
        page: 1,
        pageSize: ids.length,
        sortBy: { id: idfield, desc: false },
        filters:
          type === 'document' ? this.documentFilters : this.ticketFilters,
      });
    } catch {
      return emptySearchResult();
    }
  };

  enrichNodes = async (nodes: GroupNode[]) => {
    const itemsToEnrich = nodes;

    const docIds = itemsToEnrich
      .filter((item) => item.attributes.resourceType === 'document')
      .map((item) => item.attributes.resourceId);
    const ticketIds = itemsToEnrich
      .filter((item) => item.attributes.resourceType === 'ticket')
      .map((item) => item.attributes.resourceId);

    const [docResults, ticketResults] = await Promise.all([
      docIds.length && this.hasDocumentsAccess
        ? this.searchTypeByIds('document', docIds)
        : emptySearchResult(),
      ticketIds.length && this.hasTicketAccess
        ? this.searchTypeByIds('ticket', ticketIds)
        : emptySearchResult(),
    ]);

    const metaById = keyBy(
      [...(docResults.results || []), ...(ticketResults.results || [])],
      'id',
    );

    const enrichedNodes = itemsToEnrich.map((item) => {
      const enrichedFields = flattenSearchSelectedItemValues(
        metaById[item.attributes.resourceId]?.selected_field_values,
      );

      return {
        ...item,
        enrichedFields,
        isPermissionDenied:
          // TODO: also handle the case where a user is denied access using ACL even when their role allows it
          (!this.hasDocumentsAccess &&
            item.attributes.resourceType === 'document') ||
          (!this.hasTicketAccess && item.attributes.resourceType === 'ticket'),
      };
    }) as EnrichedGroupNode[];

    return enrichedNodes;
  };

  loadNodeAndEnrinch = async (
    nodeId: string,
    options?: { offset?: number; limit?: number },
  ) => {
    const { data, meta } = await getGroupNodeChildren(
      { nodeId },
      {
        showErrorToast: true,
        params: {
          'page[offset]': options?.offset ?? 0,
          'page[limit]': options?.limit ?? 20,
        },
      },
    );

    return {
      items: await this.enrichNodes(data),
      hasMore: !!meta && meta?.offset + meta?.count < meta?.total,
      total: meta?.total,
    };
  };
}
